// CraftsFilter.tsx
import Grid from "@material-ui/core/Grid";
import { Chip, Input, MenuItem, Select } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { useSearchParams } from "react-router-dom";
import { useTaskListContext } from "../context/taskListContext";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

const useStyles = makeStyles(() => ({
	root: {
		"& .MuiSelect-root": {
			backgroundColor: "#FFF",
			padding: "5px",
			marginBottom: 4,
		},
		"& .MuiOutlinedInput-input": {
			padding: "6px 8px",
		},
	},
	chips: {
		display: "flex",
		flexWrap: "nowrap",
		gap: 4,
		overflow: "hidden",
		maxWidth: "calc(100%)",
		minHeight: 24,
		alignItems: "center",
	},
	remainingCount: {
		backgroundColor: "#e0e0e0",
		borderRadius: 12,
		padding: "2px 6px",
		fontSize: "0.75rem",
		marginLeft: 2,
		whiteSpace: "nowrap",
		display: "flex",
		alignItems: "center",
		color: "rgba(0, 0, 0, 0.87)",
	},
	chip: {
		margin: "0",
		height: 24,
		borderRadius: 12,
	},
	defaultChip: {
		backgroundColor: "#6B7280",
		"& .MuiChip-label": {
			color: "#FFF",
		},
	},
	inputLabel: {
		position: "absolute",
		bottom: 25,
		left: 8,
		fontSize: 9.3,
		backgroundColor: "#FFF",
		color: "#0000008A",
		width: "auto",
		height: "auto",
		zIndex: 100,
		padding: "0 3px",
		borderRadius: 3,
		fontWeight: 400,
		lineHeight: "1.1876em",
		letterSpacing: "0.04208em",
		fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif',",
	},
}));

type Props = {};

export const CraftsFilter = ({}: Props) => {
	const classes = useStyles();
	const { state, actions } = useTaskListContext();
	const [searchParams, setSearchParams] = useSearchParams();

	const parseCraftParams = (craftParam: string | null): string[] => {
		if (!craftParam) return ["A"];
		if (craftParam === "A") return ["A"];
		return craftParam.split(",").filter((craft) => craft.trim() !== "");
	};

	const craft = parseCraftParams(searchParams.get("craft"));

	const handleFilter = (event: any) => {
		const selectedValue = event.target.value;
		let newSelectedValues: string[] = [];
		if (Array.isArray(selectedValue)) {
			const lastSelected = selectedValue[selectedValue.length - 1];
			if (lastSelected === "A") {
				newSelectedValues = ["A"];
			} else if (selectedValue.includes("A") && craft.includes("A")) {
				newSelectedValues = [lastSelected];
			} else {
				newSelectedValues = selectedValue.filter((v) => v !== "A");
			}
		} else {
			newSelectedValues = [selectedValue];
		}

		if (newSelectedValues.length === 0 || newSelectedValues[0] === "A") {
			searchParams.set("craft", "A");
			actions.setCraftSearchId(["A"]);
		} else {
			const craftString = newSelectedValues.join(",");
			searchParams.set("craft", craftString);
			actions.setCraftSearchId(newSelectedValues);
		}

		setSearchParams(searchParams, { replace: true });
	};

	const handleDelete = (craftToDelete: string) => {
		const updatedCrafts = craft.filter((c) => c !== craftToDelete);
		if (updatedCrafts.length === 0) {
			searchParams.set("craft", "A");
			actions.setCraftSearchId(["A"]);
		} else {
			const craftString = updatedCrafts.join(",");
			searchParams.set("craft", craftString);
			actions.setCraftSearchId(updatedCrafts);
		}
		setSearchParams(searchParams, { replace: true });
	};

	return (
		<div className={classes.root} style={{ position: "relative" }}>
			<p className={classes.inputLabel}>Crafts</p>
			<Select
				variant="outlined"
				margin="dense"
				labelId="craft-multiple-chip-label"
				id="craft-multiple-chip"
				multiple
				value={craft}
				onChange={handleFilter}
				input={<Input id="select-multiple-chip" />}
				style={{ minWidth: "100%" }}
				renderValue={(selected) => (
					<div className={classes.chips}>
						{(selected as string[]).map((craftId) => {
							const craftItem = state.craftCodes.find(
								(c) => c.id.toString() === craftId,
							);
							if (craftId === "A") {
								return (
									<Chip
										key={`craft-${craftId}`}
										label="All crafts"
										className={`${classes.chip} ${classes.defaultChip}`}
										onDelete={undefined}
										onMouseDown={(event) => {
											event.stopPropagation();
										}}
										size="small"
									/>
								);
							}
							return (
								<div
									key={`craft-${craftId}`}
									style={{
										height: 24,
										width: 24,
										borderRadius: 4,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
										background: craftItem?.color || "#FFFFFF",
										marginRight: 4,
									}}
								>
									<div
										style={{
											color: "#FFF",
											fontWeight: 600,
											fontSize: "0.875rem",
										}}
									>
										{craftItem?.description.charAt(0) || ""}
									</div>
								</div>
							);
						})}
					</div>
				)}
				MenuProps={MenuProps}
			>
				<MenuItem value="A">All crafts</MenuItem>
				{state.craftCodes
					.filter((craft) => craft.id.toString() !== "A")
					.map((craft) => (
						<MenuItem key={`logId-${craft.id}`} value={craft.id.toString()}>
							<Grid container alignItems="center">
								<div
									style={{
										height: 20,
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<div
										style={{
											height: 24,
											width: 24,
											borderRadius: 4,
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											background: craft.color || "#FFFFFF",
										}}
									>
										<div
											style={{
												color: "#FFF",
												fontWeight: 600,
												margin: "auto 0",
											}}
										>
											<div>{craft.description.charAt(0) || ""}</div>
										</div>
									</div>
								</div>
								<div style={{ marginLeft: 10 }}>{craft.description}</div>
							</Grid>
						</MenuItem>
					))}
			</Select>
		</div>
	);
};
